import axios from "axios";
import { API_ROUTES } from "../config/baseURL";
import { getToken, setToken, clearToken } from "../utils/tokenManager";

// Crear instancia de Axios con configuración base
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true, // Importante para enviar cookies (Refresh Token HttpOnly)
});

// Flag para evitar múltiples refrescos simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 🔹 Interceptor de Petición: Inyectar Headers (Auth + Multi-Empresa)
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Inyectar contexto de Sede / Empresa
    try {
      const savedSede = sessionStorage.getItem("sedeActiva");
      if (savedSede && savedSede !== "undefined") {
        const parsedSede = JSON.parse(savedSede);
        if (parsedSede?.id) config.headers["X-Sede-ID"] = String(parsedSede.id);
        if (parsedSede?.id_empresa) config.headers["X-Empresa-ID"] = String(parsedSede.id_empresa);
      }
    } catch (e) {
      console.warn("[apiClient] Error leyendo sedeActiva:", e);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 🔹 Interceptor de Respuesta: Manejar Refresh Automático (401)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || "";

    const isPatientPortal =
      requestUrl.includes("/portal-paciente") ||
      (typeof window !== "undefined" &&
        window.location.pathname.startsWith("/portal-paciente"));

    // Si es una petición del portal del paciente y da 401, redirigir al login del portal paciente
    if (error.response?.status === 401 && isPatientPortal) {
      clearToken();
      sessionStorage.removeItem("patient_token");
      sessionStorage.removeItem("patient_user");
      if (
        typeof window !== "undefined" &&
        window.location.pathname !== "/portal-paciente/login"
      ) {
        window.location.href = "/portal-paciente/login";
      }
      return Promise.reject(error);
    }

    const isAuthRoute =
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/refresh");

    // Si es un 401 y no es una ruta de autenticación directa ni un reintento
    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthRoute) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(API_ROUTES.refresh, {}, { withCredentials: true });
        
        if (res.data?.success && res.data?.data?.access_token) {
          const newToken = res.data.data.access_token;
          setToken(newToken);
          processQueue(null, newToken);
          isRefreshing = false;
          
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          throw new Error("Refresh fallido");
        }
      } catch (err) {
        processQueue(err, null);
        isRefreshing = false;
        clearToken();
        sessionStorage.removeItem("hasSession");
        if (typeof window !== "undefined") {
          if (window.location.pathname.startsWith("/portal-paciente")) {
            window.location.href = "/portal-paciente/login";
          } else if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
